// Veřejná navigační service – operace openRoot, openModal, replaceModal, openOverlayRoot, closeTop, closeTo

import { useCallback } from 'react';
import { useNavigationContext } from './useNavigationContext';
import type { AgendaHandle } from '../core/agendaDefinition';
import type { ParamCodecMap, ParamInput } from '../core/paramCodec';

/**
 * Hook pro navigační operace.
 * Vrací typově bezpečné metody pro otevírání a zavírání vrstev.
 *
 * Metody `closeTop`, `closeTo`, `closeLayer`, `openRoot`, `openModal` a
 * `replaceModal` jsou async – před provedením mohou konzultovat close guardy
 * registrované komponentami přes `useLayerCloseGuard`.
 */
export function useNavigator() {
  const { kernel } = useNavigationContext();

  const openRoot = useCallback(
    async <TOpen extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen>,
      input: ParamInput<TOpen>,
      component?: React.ComponentType<{ layerId: string }>
    ): Promise<void> => {
      await kernel.openRoot(agenda, input, component);
    },
    [kernel]
  );

  const openModal = useCallback(
    async <TOpen extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen>,
      input: ParamInput<TOpen>,
      component?: React.ComponentType<{ layerId: string }>
    ): Promise<void> => {
      await kernel.openModal(agenda, input, component);
    },
    [kernel]
  );

  const replaceModal = useCallback(
    async <TOpen extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen>,
      input: ParamInput<TOpen>,
      component?: React.ComponentType<{ layerId: string }>
    ): Promise<void> => {
      await kernel.replaceModal(agenda, input, component);
    },
    [kernel]
  );

  const openOverlayRoot = useCallback(
    async <TOpen extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen>,
      input: ParamInput<TOpen>,
      component?: React.ComponentType<{ layerId: string }>
    ): Promise<void> => {
      await kernel.openOverlayRoot(agenda, input, component);
    },
    [kernel]
  );

  const closeTop = useCallback(async (): Promise<void> => {
    await kernel.closeTop();
  }, [kernel]);

  const closeTo = useCallback(
    async (layerId: string): Promise<void> => {
      await kernel.closeTo(layerId);
    },
    [kernel]
  );

  const closeLayer = useCallback(
    async (layerId: string): Promise<void> => {
      await kernel.closeLayer(layerId);
    },
    [kernel]
  );

  return {
    openRoot,
    openModal,
    replaceModal,
    openOverlayRoot,
    closeTop,
    closeTo,
    closeLayer,
  };
}
